import {
  Component,
  HostBinding,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  Output,
  EventEmitter,
  OnChanges,
  Input,
  SimpleChanges
} from '@angular/core';
import { hexToRgb, rgbToHex, rgbToHsv, hsvToHex } from './color-utils';

@Component({
  selector: 'suka-color-palette',
  template: `
    <canvas
      #canvas
      class="color-palette"
      width="250"
      height="250"
      (mousedown)="onMouseDown($event)"
      (mousemove)="onMouseMove($event)"
    >
    </canvas>
  `
})
export class ColorPalette implements AfterViewInit, OnChanges {
  @HostBinding('class.color-palette-container') baseClass = true;

  @Input() hue: string;

  @Input() color: string;
  @Output() colorChange: EventEmitter<string> = new EventEmitter(true);

  @ViewChild('canvas', {static: false}) canvas: ElementRef<HTMLCanvasElement>;

  private ctx: CanvasRenderingContext2D;

  private mousedown = false;

  public selectedPosition: { x: number; y: number };

  draw() {
    if (!this.ctx) {
      this.ctx = this.canvas.nativeElement.getContext('2d');
    }

    const width = this.canvas.nativeElement.width;
    const height = this.canvas.nativeElement.height;

    if (this.hue) {
      this.ctx.fillStyle = this.hue;
    } else {
      this.ctx.fillStyle = '#ff0000';
    }

    this.ctx.fillRect(0, 0, width, height);

    const whiteGrad = this.ctx.createLinearGradient(0, 0, width, 0);
    whiteGrad.addColorStop(0, 'rgba(255,255,255,1)');
    whiteGrad.addColorStop(1, 'rgba(255,255,255,0)');

    this.ctx.fillStyle = whiteGrad;
    this.ctx.fillRect(0, 0, width, height);

    const blackGrad = this.ctx.createLinearGradient(0, 0, 0, height);
    blackGrad.addColorStop(0, 'rgba(0,0,0,0)');
    blackGrad.addColorStop(1, 'rgba(0,0,0,1)');

    this.ctx.fillStyle = blackGrad;
    this.ctx.fillRect(0, 0, width, height);

    if (this.selectedPosition) {
      this.ctx.strokeStyle = 'white';
      this.ctx.fillStyle = 'white';
      this.ctx.beginPath();
      this.ctx.arc(
        this.selectedPosition.x,
        this.selectedPosition.y,
        5,
        0,
        2 * Math.PI
      );
      this.ctx.lineWidth = 3;
      this.ctx.stroke();
      this.ctx.closePath();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.color && !changes.color.isFirstChange()) {
      const rgbColor = hexToRgb(this.color);

      if (rgbColor) {
        const hsv = rgbToHsv(rgbColor.r, rgbColor.g, rgbColor.b);
        this.selectedPosition = {
          x: (hsv.s * 250) / 100,
          y: 250 - (hsv.v * 250) / 100
        };
        this.draw();
      }
    } else if (changes.hue && !changes.hue.isFirstChange()) {
      this.draw();

      const pos = this.selectedPosition;

      if (pos) {
        this.colorChange.emit(this.getColorAtPosition(pos.x, pos.y));
      }
    }
  }

  ngAfterViewInit() {
    if (this.color) {
      const rgbColor = hexToRgb(this.color);

      if (rgbColor) {
        const hsv = rgbToHsv(rgbColor.r, rgbColor.g, rgbColor.b);
        this.selectedPosition = {
          x: (hsv.s * 250) / 100,
          y: 250 - (hsv.v * 250) / 100
        };
      }
    }
    this.draw();
  }

  @HostListener('window:mouseup', ['$event'])
  onMouseUp(evt: MouseEvent) {
    this.mousedown = false;
  }

  onMouseDown(evt: MouseEvent) {
    this.mousedown = true;
    const xPos = evt.offsetX < 0 ? 0 : evt.offsetX >= 250 ? 249 : evt.offsetX;
    const yPos = evt.offsetY < 0 ? 0 : evt.offsetY >= 250 ? 249 : evt.offsetY;

    this.selectedPosition = {
      x: xPos,
      y: yPos,
    };
    this.draw();
    this.emitColor(xPos, yPos);
  }

  onMouseMove(evt: MouseEvent) {
    if (this.mousedown) {
      const xPos = evt.offsetX < 0 ? 0 : evt.offsetX >= 250 ? 249 : evt.offsetX;
      const yPos = evt.offsetY < 0 ? 0 : evt.offsetY >= 250 ? 249 : evt.offsetY;

      this.selectedPosition = {
        x: xPos,
        y: yPos,
      };
      this.draw();
      this.emitColor(xPos, yPos);
    }
  }

  emitColor(x: number, y: number) {
    const rgbaColor = this.getColorAtPosition(x, y);
    this.colorChange.emit(rgbaColor);
  }

  getColorAtPosition(x: number, y: number) {
    const imageData = this.ctx.getImageData(x, y, 1, 1).data;
    return rgbToHex(imageData[0], imageData[1], imageData[2]);
  }
}
